import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | verify
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const submitReg = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/register", { email, password, name });
      if (response.data?.email_sent) toast.success("Enviamos um código para seu e-mail");
      else toast.error("A conta foi preparada, mas o e-mail não pôde ser enviado. Verifique o serviço de e-mail ou tente reenviar.");
      setStep("verify");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/verify", { email, code });
      toast.success("Conta ativada. Entre com sua senha.");
      navigate("/login");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const response = await api.post("/auth/resend-code", { email });
      if (response.data?.email_sent) toast.success("Novo código enviado");
      else toast.error("O código não pôde ser enviado. Confirme a configuração de e-mail com o administrador.");
    } catch (err) {
      toast.error(fmtError(err));
    }
  };

  return (
    <div className="min-h-[calc(100vh-49px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-16 bg-foreground text-background">
        <div>
          <Link
            to="/login"
            aria-label="Voltar para a página inicial"
            data-testid="register-logo-home"
            className="inline-block text-background no-underline transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div className="font-display text-3xl font-black tracking-tighter">
              SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span>
            </div>
            <div className="label-eyebrow mt-2 text-background/60">
              sistema de provas e controle acadêmico
            </div>
          </Link>
        </div>
        <div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-[1.05]">
            Uma conta para
            <br />
            <span className="text-primary">todas as suas turmas.</span>
          </h1>
        </div>
        <div className="label-eyebrow text-background/40">● verificação de e-mail obrigatória</div>
      </div>
      <div className="relative flex items-center justify-center p-8 pt-24 lg:pt-8">
        <Link
          to="/login"
          aria-label="Voltar para a página inicial"
          data-testid="register-logo-home-mobile"
          className="absolute left-8 top-8 lg:hidden font-display text-2xl font-black tracking-tighter text-foreground no-underline transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span>
        </Link>
        {step === "form" ? (
          <form
            onSubmit={submitReg}
            className="w-full max-w-sm space-y-6"
            data-testid="register-form"
          >
            <div>
              <div className="label-eyebrow">criar conta</div>
              <h2 className="font-display text-4xl font-black tracking-tighter mt-2">
                Cadastrar.
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                Use o mesmo e-mail do convite. Enviaremos um código de 6 dígitos para validar sua conta.
              </p>
            </div>
            <label className="block">
              <span className="label-eyebrow">nome</span>
              <Input
                data-testid="reg-name"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 rounded-none font-mono-data"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow">e-mail</span>
              <Input
                data-testid="reg-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-none font-mono-data"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow">senha ou frase-senha (mín. 15)</span>
              <Input
                data-testid="reg-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={15}
                maxLength={128}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-none font-mono-data"
              />
            </label>
            <Button
              data-testid="reg-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs"
            >
              <UserPlus size={14} weight="bold" className="mr-2" />
              {loading ? "Enviando…" : "Receber código"}
            </Button>
            <div className="text-sm text-muted-foreground text-center">
              Já tem conta?{" "}
              <Link
                to="/login"
                className="text-primary font-semibold hover:underline"
              >
                Entrar
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={submitVerify}
            className="w-full max-w-sm space-y-6"
            data-testid="verify-form"
          >
            <div>
              <div className="label-eyebrow">passo 2</div>
              <h2 className="font-display text-4xl font-black tracking-tighter mt-2">
                Confirme.
              </h2>
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                <EnvelopeSimple size={16} weight="duotone" />
                Enviamos um código para <strong>{email}</strong>
              </p>
            </div>
            <label className="block">
              <span className="label-eyebrow">código de 6 dígitos</span>
              <Input
                data-testid="verify-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-1 rounded-none font-mono-data text-center tracking-[0.5em] text-2xl"
              />
            </label>
            <Button
              data-testid="verify-submit"
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs"
            >
              {loading ? "Verificando…" : "Confirmar conta"}
            </Button>
            <button
              type="button"
              onClick={resend}
              data-testid="verify-resend"
              className="w-full text-sm text-primary hover:underline"
            >
              Reenviar código
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
